import { useCallback } from 'react';

import Platform from '@lyearn/core/utils/Platform';
import usePagination from '@/hooks/usePagination';
import { OrderEnum, UserNotificationsFilters, UserNotificationStatusEnum } from '@/types/schema';

import { NotificationDetailsFragment } from '../graphql/fragments/generated/NotificationDetails';
import { useReadNotificationsMutation } from '../graphql/mutations/generated/readNotifications';
import { useGetNotificationQuery } from '../graphql/queries/generated/getNotifications';

const isNative = Platform.OS !== 'web';

function useNotifications(
  toggleTray?: () => void,
  filters?: UserNotificationsFilters,
  pause?: boolean,
) {
  const { data, fetchMore, isFetching, isFetchingMore } = usePagination(
    useGetNotificationQuery,
    {
      filters,
      sort: {
        field: 'createdAt',
        order: OrderEnum.Desc,
      },
    },
    { pause, edgeKey: 'userNotifications', pageSize: 10 },
  );

  const [, readAllNotifications] = useReadNotificationsMutation();

  const readNotificationRead = useCallback(
    (userNotificationId: string) => {
      readAllNotifications({
        input: {
          userNotificationId,
        },
      });
    },
    [readAllNotifications],
  );

  const onNotificationItemClick = useCallback(
    (userNotification: NotificationDetailsFragment) => {
      if (userNotification.status === UserNotificationStatusEnum.Unread) {
        readNotificationRead(userNotification.id);
      }
      toggleTray?.();
    },
    [readNotificationRead, toggleTray],
  );

  return {
    isFetching,
    isFetchingMore,
    userNotifications: data,
    fetchMore,
    onNotificationItemClick,
  };
}

export default useNotifications;
